import { supabase } from './supabase';

const cache = new Map<string, string>();
const resetListeners = new Set<() => void>();
let generation = 0;
let pending = new Map<string, Set<number>>();
let flushing: Promise<void> | null = null;

export function normalizePayrollMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return 0;
  return Math.trunc(minutes);
}

/** Turn a decimal-hour total back into whole minutes so the database can label it. */
export function payrollMinutesFromHours(hours: number): number {
  if (!Number.isFinite(hours)) return 0;
  return Math.round(hours * 60);
}

function cacheKey(businessId: string, minutes: number): string {
  return `${businessId}:${minutes}`;
}

export function peekPayrollHoursLabel(businessId: string, minutes: number): string | undefined {
  return cache.get(cacheKey(businessId, normalizePayrollMinutes(minutes)));
}

export function subscribePayrollHoursLabelReset(listener: () => void): () => void {
  resetListeners.add(listener);
  return () => {
    resetListeners.delete(listener);
  };
}

export function clearPayrollHoursLabelCache(): void {
  generation += 1;
  cache.clear();
  pending = new Map();
  resetListeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  window.addEventListener('payrollPeriodSettingsChanged', () => {
    clearPayrollHoursLabelCache();
  });
}

function requeue(batches: Map<string, Set<number>>): void {
  for (const [businessId, minutes] of batches) {
    let set = pending.get(businessId);
    if (!set) {
      set = new Set();
      pending.set(businessId, set);
    }
    minutes.forEach((value) => set!.add(value));
  }
}

async function flush(gen: number): Promise<void> {
  const batches = pending;
  pending = new Map();
  if (gen !== generation) {
    requeue(batches);
    return;
  }

  await Promise.all([...batches.entries()].map(async ([businessId, minutes]) => {
    if (minutes.size === 0 || !supabase) return;
    const values = [...minutes];
    const { data, error } = await supabase.rpc('payroll_hours_labels', {
      p_business_id: businessId,
      p_minutes: values,
    });
    if (gen !== generation) {
      requeue(new Map([[businessId, minutes]]));
      return;
    }
    if (error || !data || typeof data !== 'object') {
      if (error) console.error('Error loading payroll hour labels:', error);
      return;
    }
    for (const [key, label] of Object.entries(data as Record<string, string>)) {
      if (typeof label === 'string') cache.set(cacheKey(businessId, Number(key)), label);
    }
  }));
}

function scheduleFlush(): Promise<void> {
  if (!flushing) {
    const gen = generation;
    flushing = Promise.resolve()
      .then(() => flush(gen))
      .finally(() => {
        flushing = null;
        if (pending.size > 0) void scheduleFlush();
      });
  }
  return flushing;
}

export function ensurePayrollHoursLabels(businessId: string, minutes: number[]): Promise<void> {
  const gen = generation;
  const needed: number[] = [];
  for (const raw of minutes) {
    const value = normalizePayrollMinutes(raw);
    if (cache.has(cacheKey(businessId, value))) continue;
    needed.push(value);
    let set = pending.get(businessId);
    if (!set) {
      set = new Set();
      pending.set(businessId, set);
    }
    set.add(value);
  }
  if (needed.length === 0) return Promise.resolve();
  return scheduleFlush().then(() => {
    if (gen !== generation) return;
    const missing = needed.filter((value) => !cache.has(cacheKey(businessId, value)));
    if (missing.length === 0) return;
    return ensurePayrollHoursLabels(businessId, missing);
  });
}

export async function fetchPayrollHoursLabels(
  businessId: string,
  minutes: number[],
): Promise<Record<string, string>> {
  await ensurePayrollHoursLabels(businessId, minutes);
  const labels: Record<string, string> = {};
  for (const raw of minutes) {
    const value = normalizePayrollMinutes(raw);
    const label = peekPayrollHoursLabel(businessId, value);
    if (label) labels[String(value)] = label;
  }
  return labels;
}
